/** Tipos y utilidades compartidas entre importación PDF (alta y edición de empresa). */

/** Errores por campo devueltos en `importacionError` (lista en respuesta de import). */
export type ImportPdfFieldErrors = {
  rfc?: string;
  razonSocial?: string;
  regimen?: string;
  razonSocialLevel?: string;
  rfcLevel?: string;
  /** P. ej. `WARNING` para icono de aviso en régimen. */
  regimenLevel?: string;
};

export interface EmpresaConfigApi {
  info?: Record<string, any>;
  regimenes?: any[];
  actividadesEconomicas?: any[];
  obligacionesFiscales?: any[];
  archivosSat?: any[];
  archivosSellos?: any[];
  /** Resumen QUO Contabilita (p. ej. `{ id, usuario, contrasena }`). */
  quoContabilita?: any;
  /** Resumen QUO Facturita (p. ej. `{ usuario, contrasena }`). */
  quoFacturita?: any;
  /** QUO Contabilita (objeto o arreglo con un elemento; campos típicos quoId, usuario, contrasena). */
  usuariosQUO?: any;
  /** QUO Facturita (JSON / lista según API). */
  usuariosQUOFacturita?: any;
}

export function str(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Cabecera binaria %PDF */
export async function fileHasPdfMagic(file: File): Promise<boolean> {
  if (file.size < 4) return false;
  const head = await file.slice(0, 4).arrayBuffer();
  const s = new Uint8Array(head);
  return s[0] === 0x25 && s[1] === 0x50 && s[2] === 0x44 && s[3] === 0x46;
}

/** Cuerpo de respuesta axios GET/POST /config → objeto con `info` (y listas opcionales). */
export function normalizeEmpresaConfigPayload(raw: unknown): EmpresaConfigApi | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.info != null && typeof r.info === 'object') {
    return r as EmpresaConfigApi;
  }
  const looksLikeInfo =
    'razonSocial' in r ||
    'rfc' in r ||
    ('id' in r && (typeof r.id === 'number' || typeof r.id === 'string'));
  if (looksLikeInfo) {
    return { info: r as Record<string, any> };
  }
  return null;
}

/** Cuerpo de respuesta/import: puede venir como config o envuelto en `data`. */
export function parseImportConfigBody(raw: unknown): EmpresaConfigApi | null {
  if (raw == null) return null;
  let parsed = normalizeEmpresaConfigPayload(raw);
  if (parsed?.info) return parsed;
  if (typeof raw === 'object' && raw !== null && 'data' in raw) {
    parsed = normalizeEmpresaConfigPayload((raw as any).data);
    if (parsed?.info) return parsed;
  }
  return null;
}

/** Mensaje no bloqueante del backend (p. ej. warning con HTTP error y payload útil). */
export function extractImportWarningFromBody(raw: unknown): string | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as any;
  const pick = (x: any) => {
    const w = x?.warning ?? x?.warn ?? x?.aviso;
    if (typeof w === 'string' && w.trim()) return w.trim();
    return null;
  };
  return pick(r) ?? pick(r.data);
}

function mapImportErrorCampoToField(campoRaw: unknown): keyof ImportPdfFieldErrors | null {
  const s = str(campoRaw)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
  if (!s) return null;
  if (s.includes('rfc')) return 'rfc';
  if (s.includes('razonsocial') || s.includes('razon')) return 'razonSocial';
  if (s.includes('regimen')) return 'regimen';
  return null;
}

/** Lista `importacionError` en raíz o dentro de `data`: ítems con campo + mensaje. */
export function extractImportacionFieldErrors(raw: unknown): ImportPdfFieldErrors {
  const out: ImportPdfFieldErrors = {};
  const roots: any[] = [];
  if (raw != null && typeof raw === 'object') {
    roots.push(raw);
    const d = (raw as any).data;
    if (d != null && typeof d === 'object' && !roots.includes(d)) roots.push(d);
  }
  for (const root of roots) {
    const list = root?.importacionError ?? root?.importacionErrors;
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (typeof item === 'string') {
        const t = item.trim();
        if (t) out.regimen = out.regimen ? `${out.regimen}. ${t}` : t;
        continue;
      }
      if (item == null || typeof item !== 'object') continue;
      const campo =
        (item as any).campo ??
        (item as any).field ??
        (item as any).atributo ??
        (item as any).property ??
        (item as any).path ??
        (item as any).key;
      const msgRaw =
        (item as any).mensaje ??
        (item as any).message ??
        (item as any).error ??
        (item as any).defaultMessage ??
        (item as any).text;
      if (msgRaw == null || String(msgRaw).trim() === '') continue;
      const fieldKey = mapImportErrorCampoToField(campo);
      if (!fieldKey) continue;
      const msg = String(msgRaw).trim();
      out[fieldKey] = out[fieldKey] ? `${out[fieldKey]}. ${msg}` : msg;
      if (fieldKey === 'regimen') {
        const lvl = str((item as any).level).trim();
        if (lvl) {
          if (!out.regimenLevel) out.regimenLevel = lvl;
          else if (lvl.toUpperCase() === 'WARNING') out.regimenLevel = lvl;
        }
      } else if (fieldKey === 'rfc') {
        const lvl = str((item as any).level).trim();
        if (lvl) {
          if (!out.rfcLevel) out.rfcLevel = lvl;
          else if (lvl.toUpperCase() === 'WARNING') out.rfcLevel = lvl;
        }
      } else if (fieldKey === 'razonSocial') {
        const lvl = str((item as any).level).trim();
        if (lvl) {
          if (!out.razonSocialLevel) out.razonSocialLevel = lvl;
          else if (lvl.toUpperCase() === 'WARNING') out.razonSocialLevel = lvl;
        }
      }
    }
  }
  return out;
}

function unwrapKeyedQuoSource(u: unknown): Record<string, unknown> | null {
  if (
    Array.isArray(u) &&
    u.length > 0 &&
    u[0] != null &&
    typeof u[0] === 'object' &&
    !Array.isArray(u[0])
  ) {
    return u[0] as Record<string, unknown>;
  }
  if (typeof u === 'object' && u !== null && !Array.isArray(u)) {
    return u as Record<string, unknown>;
  }
  return null;
}

function pickQuoScalarFromSources(sources: (Record<string, unknown> | null | undefined)[], keys: readonly string[]): string {
  for (const obj of sources) {
    if (!obj) continue;
    for (const k of keys) {
      const v = obj[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

/** Detecta campos típicos de contraseña QUO (nombre de propiedad JSON). */
export function isQuoPasswordFieldKey(key: string): boolean {
  const lower = key.toLowerCase();
  return lower === 'password' || lower === 'contrasena' || lower === 'contraseña' || lower === 'pqc' || lower === 'pqf';
}

/**
 * Eco del GET (null, vacío o solo máscara tipo `*****`): no es contraseña real.
 * Se elimina del estado para no reenviarla en el PUT.
 */
export function isClientEchoQuoPassword(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== 'string') return false;
  const t = value.trim();
  if (t.length === 0) return true;
  return /^[*•‧·･]+$/.test(t);
}

/** PUT: quitar máscaras/ecos; conservar `null` (borrado explícito desde el formulario QUO). */
export function isPutStrippableQuoPasswordEcho(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const t = value.trim();
  if (t.length === 0) return true;
  return /^[*•‧·･]+$/.test(t);
}

export function stripQuoPasswordEchoKeysDeep(
  value: unknown,
  shouldStripValue: (_key: string, val: unknown) => boolean,
): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(v => stripQuoPasswordEchoKeysDeep(v, shouldStripValue));
  if (typeof value !== 'object') return value;
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (isQuoPasswordFieldKey(k) && shouldStripValue(k, v)) continue;
    out[k] = stripQuoPasswordEchoKeysDeep(v, shouldStripValue);
  }
  return out;
}

/** Tras GET/import: quita contraseñas enmascaradas o vacías (conserva texto real, p. ej. import PDF). */
export function sanitizeEmpresaConfigQuoEchoPasswordsForClientState(config: EmpresaConfigApi): EmpresaConfigApi {
  const pred = (_k: string, v: unknown) => isClientEchoQuoPassword(v);
  const next: EmpresaConfigApi = { ...config };
  if (next.info != null && typeof next.info === 'object' && !Array.isArray(next.info)) {
    next.info = stripQuoPasswordEchoKeysDeep(next.info, pred) as Record<string, any>;
  }
  if (next.quoContabilita != null) next.quoContabilita = stripQuoPasswordEchoKeysDeep(next.quoContabilita, pred);
  if (next.quoFacturita != null) next.quoFacturita = stripQuoPasswordEchoKeysDeep(next.quoFacturita, pred);
  if (next.usuariosQUO != null) next.usuariosQUO = stripQuoPasswordEchoKeysDeep(next.usuariosQUO, pred);
  if (next.usuariosQUOFacturita != null) {
    next.usuariosQUOFacturita = stripQuoPasswordEchoKeysDeep(next.usuariosQUOFacturita, pred);
  }
  return next;
}

/** Lee el payload antes de sanitizar: hay valor en campo contraseña (incluye `*****`). */
export function empresaConfigQuoContabilitaShowsStoredPassword(ec: EmpresaConfigApi | null): boolean {
  if (!ec) return false;
  const uqo = unwrapKeyedQuoSource(ec.usuariosQUO);
  const qc = unwrapKeyedQuoSource(ec.quoContabilita);
  const info = ec.info != null && typeof ec.info === 'object' ? (ec.info as Record<string, unknown>) : null;
  const pwd = pickQuoScalarFromSources([uqo, qc, info], ['contrasena', 'contraseña', 'Contraseña', 'password', 'pqc']);
  return pwd !== '';
}

export function empresaConfigQuoFacturitaShowsStoredPassword(ec: EmpresaConfigApi | null): boolean {
  if (!ec) return false;
  const uf = unwrapKeyedQuoSource(ec.usuariosQUOFacturita);
  const qf = unwrapKeyedQuoSource(ec.quoFacturita);
  const info = ec.info != null && typeof ec.info === 'object' ? (ec.info as Record<string, unknown>) : null;
  const pwd = pickQuoScalarFromSources([uf, qf, info], ['contrasena', 'contraseña', 'Contraseña', 'password', 'pqf']);
  return pwd !== '';
}

/** Barrera antes de serializar multipart. */
export function applyQuoEchoPasswordStrippersForEmpresaPutJson(forJson: Record<string, unknown>): void {
  const pred = (_k: string, v: unknown) => isPutStrippableQuoPasswordEcho(v);
  for (const prop of ['info', 'quoContabilita', 'quoFacturita', 'usuariosQUO', 'usuariosQUOFacturita'] as const) {
    if (!(prop in forJson) || forJson[prop] == null) continue;
    forJson[prop] = stripQuoPasswordEchoKeysDeep(forJson[prop], pred);
  }
}
